<template>
  <div>
    <p class="text-muted">
      Packages decide which form sections the client has to complete and which documents are
      requested. Default packages are applied automatically from the account's client type,
      account type and client category; others can be added by hand.
    </p>

    <b-alert v-if="pendingPackageChanges.length" show variant="warning" class="p-1 mb-1">
      <feather-icon icon="AlertTriangleIcon" class="mr-50" />
      The package settings have changed since they were applied to this onboarding:
      <span v-for="(change, index) in pendingPackageChanges" :key="'chg' + index" class="d-block ml-2">
        • <strong>{{ change.PackageName }}</strong><template v-if="change.Item"> — {{ change.Item }}</template>: {{ change.Change }}
      </span>
      <b-button variant="warning" size="sm" class="mt-1" :disabled="syncing" @click="syncPackages">
        <b-spinner v-if="syncing" small class="mr-25" />
        <feather-icon v-else icon="RefreshCwIcon" size="14" class="mr-25" />
        Apply settings to this onboarding
      </b-button>
    </b-alert>

    <b-table
      :items="appliedPackages"
      :fields="appliedFields"
      show-empty
      empty-text="No packages applied to this onboarding yet."
      responsive
      class="mb-2"
    >
      <template #cell(Source)="{ value }">
        <b-badge :variant="sourceVariant(value)">{{ value }}</b-badge>
      </template>
      <template #cell(AppliedAt)="{ value }">
        {{ formatDateTime(value) }}
      </template>
      <template #cell(actions)="{ item }">
        <b-button
          v-if="item.Source !== 'Legacy'"
          variant="flat-danger"
          size="sm"
          title="Remove package"
          :disabled="removingId === item.id"
          @click="removePackage(item)"
        >
          <b-spinner v-if="removingId === item.id" small />
          <feather-icon v-else icon="Trash2Icon" size="14" />
        </b-button>
      </template>
    </b-table>

    <h5>Add a package</h5>
    <b-row class="align-items-end">
      <b-col cols="12" md="6">
        <b-form-group label="Package" class="mb-1">
          <v-select
            v-model="selectedPackage"
            :options="availablePackages"
            label="Name"
            placeholder="Choose a package…"
            :clearable="true"
          >
            <template #option="option">
              {{ option.Name }}
              <small class="text-muted ml-25">{{ option.Code }}</small>
              <b-badge v-if="option.IsDefault" variant="light-primary" class="ml-25">Default</b-badge>
            </template>
            <template #selected-option="option">
              {{ option.Name }}
              <small class="text-muted ml-25">{{ option.Code }}</small>
            </template>
          </v-select>
          <small v-if="selectedPackage && selectedPackage.Description" class="text-muted">
            {{ selectedPackage.Description }}
          </small>
        </b-form-group>
      </b-col>
      <b-col cols="12" md="3">
        <b-form-checkbox v-model="notify" class="mb-1">Notify client by email</b-form-checkbox>
      </b-col>
      <b-col cols="12" md="3">
        <b-button variant="primary" block class="mb-1" :disabled="!selectedPackage || adding" @click="addPackage">
          <b-spinner v-if="adding" small class="mr-25" />
          <feather-icon v-else icon="PlusIcon" size="14" class="mr-25" />
          Add package
        </b-button>
      </b-col>
    </b-row>
    <small v-if="!availablePackages.length" class="text-muted">Every configured package is already applied.</small>
  </div>
</template>

<script>
import vSelect from "vue-select";
import onboardingMixin from "./onboardingMixin";

export default {
  name: "OnboardingPackagesTab",

  components: { vSelect },

  mixins: [onboardingMixin],

  props: {
    onboardingId: { type: Number, required: true },
    appliedPackages: { type: Array, default: () => [] },
    availablePackages: { type: Array, default: () => [] },
    pendingPackageChanges: { type: Array, default: () => [] },
    sectionCatalogue: { type: Array, default: () => [] },
  },

  data() {
    return {
      selectedPackage: null,
      notify: true,
      adding: false,
      syncing: false,
      removingId: null,
      appliedFields: [
        { key: "PackageName", label: "Package" },
        { key: "Source", label: "Source" },
        { key: "AppliedBy", label: "Applied by" },
        { key: "AppliedAt", label: "Applied at" },
        { key: "actions", label: "" },
      ],
    };
  },

  methods: {
    sectionTitle(key) {
      const section = this.sectionCatalogue.find((s) => s.key === key);
      return section ? section.title : key;
    },

    // Builds the confirm body: what the package would add to this onboarding.
    deltaMessage(preview, pkg) {
      const h = this.$createElement;
      const requirements = (preview.requirements || []).map((r) => {
        let suffix = "";
        if (r.PersonName) suffix = ` (${r.PersonName})`;
        else if (r.Scope && r.Scope !== "Account") suffix = ` (${r.Scope})`;
        return r.Name + suffix;
      });
      const sections = (preview.formSections || []).map((key) => this.sectionTitle(key));
      const formItems = (preview.formItems || []).map((item) => {
        const section = item.section || this.sectionTitle(item.sectionKey);
        const label = item.label || item.kind || "";
        return `${section} / ${label}${item.required ? " (required)" : ""}`;
      });

      const children = [h("p", { class: "mb-50" }, [`Add the package "${pkg.Name}" to this onboarding?`])];
      if (requirements.length) {
        children.push(h("p", { class: "mb-25 font-weight-bold" }, ["Documents to be requested:"]));
        children.push(h("ul", { class: "mb-50" }, requirements.map((name) => h("li", [name]))));
      }
      if (sections.length) {
        children.push(h("p", { class: "mb-25 font-weight-bold" }, ["Form sections to be added:"]));
        children.push(h("ul", { class: "mb-50" }, sections.map((name) => h("li", [name]))));
      }
      if (formItems.length) {
        children.push(h("p", { class: "mb-25 font-weight-bold" }, ["Form items to be added:"]));
        children.push(h("ul", { class: "mb-50" }, formItems.map((name) => h("li", [name]))));
      }
      if (!requirements.length && !sections.length && !formItems.length) {
        children.push(h("p", { class: "text-muted mb-50" }, ["Nothing new — everything in this package is already covered."]));
      }
      if (this.notify && (requirements.length || sections.length || formItems.length)) {
        children.push(h("p", { class: "mb-0" }, ["The active contacts will be emailed about the new items."]));
      }
      return h("div", { class: "text-left" }, children);
    },

    addPackage() {
      const pkg = this.selectedPackage;
      if (!pkg || this.adding) return;

      this.adding = true;
      this.dispatchOnboarding("previewPackage", { onboardingId: this.onboardingId, packageId: pkg.id })
        .then((res) =>
          this.$bvModal.msgBoxConfirm([this.deltaMessage(res.data || {}, pkg)], {
            title: "Add Package",
            okVariant: "primary",
            okTitle: "Add",
            cancelTitle: "Cancel",
            centered: true,
          })
        )
        .then((confirmed) => {
          if (!confirmed) return null;
          return this.dispatchOnboarding("addPackage", {
            onboardingId: this.onboardingId,
            packageId: pkg.id,
            notify: this.notify,
          }).then((res) => {
            this.toastSuccess((res.data && res.data.message) || "Package added.", "Package Added");
            this.selectedPackage = null;
            this.$emit("refresh");
          });
        })
        .catch((err) => this.toastError(err, "Failed to add the package."))
        .finally(() => {
          this.adding = false;
        });
    },

    removePackage(item) {
      this.confirmBox(
        `Remove the package "${item.PackageName}"? Documents requested only by this package are withdrawn (approved files are kept) and its form sections are no longer required.`,
        { title: "Remove Package", okVariant: "danger", okTitle: "Remove" }
      ).then((confirmed) => {
        if (!confirmed) return;
        this.removingId = item.id;
        this.dispatchOnboarding("removePackage", { onboardingId: this.onboardingId, appliedPackageId: item.id })
          .then((res) => {
            const withdrawn = (res.data && res.data.withdrawn) || [];
            this.toastSuccess(
              ((res.data && res.data.message) || "Package removed.") +
                (withdrawn.length ? ` Withdrawn: ${withdrawn.join(", ")}.` : ""),
              "Package Removed"
            );
            this.$emit("refresh");
          })
          .catch((err) => this.toastError(err, "Failed to remove the package."))
          .finally(() => {
            this.removingId = null;
          });
      });
    },

    syncPackages() {
      this.syncing = true;
      this.dispatchOnboarding("syncPackages", { onboardingId: this.onboardingId })
        .then((res) => {
          this.toastSuccess((res.data && res.data.message) || "Packages synced.", "Packages Synced");
          this.$emit("refresh");
        })
        .catch((err) => this.toastError(err, "Failed to apply the package settings."))
        .finally(() => {
          this.syncing = false;
        });
    },
  },
};
</script>
